import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { API_URL } from '..';
import MasterLayout from '../masterLayout/MasterLayout';

const EditCustomerPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customerData, setCustomerData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        status: 'active',
        postcode: ''
    });
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState({
        status: false,
        reason: '',
        desc: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState([]);

    // Mapbox API configuration
    const MAPBOX_TOKEN = 'pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ'; // Replace with your Mapbox access token

    useEffect(() => {
        fetchCustomer();
    }, []);

    const fetchCustomer = async () => {
        try {
            const response = await fetch(`${API_URL}/api/customer/fetch-single/${id}`, {
                method: 'GET',
                headers: { 'Accept': '*/*', 'Content-Type': 'application/json' }
            });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            if (response.status === 200) {
                setCustomerData({
                    name: data.customer.name || '',
                    email: data.customer.email || '',
                    phone: data.customer.phone || '',
                    address: data.customer.address || '',
                    status: data.customer.status || 'active',
                    postcode: data.customer.postcode || ''
                });
            } else {
                throw new Error(data.message || 'Failed to fetch customer');
            }
        } catch (error) {
            setMessage({ status: true, reason: 'error', desc: 'Failed to fetch customer: ' + error.message });
        }
    };

    const handleInputChange = (field, value) => {
        setCustomerData({ ...customerData, [field]: value });
        setErrors({ ...errors, [field]: '' });
    };


    const handleAddressChange = (e) => {
        if (e) {
            setCustomerData({ ...customerData, address: e.target.value });
            setErrors({ ...errors, address: '' });
        } else {
            setCustomerData({ ...customerData, address: '' });
        }
        setAddressSuggestions([]);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!customerData.name) newErrors.name = 'Name is required';
        if (!customerData.email || !/\S+@\S+\.\S+/.test(customerData.email)) newErrors.email = 'Valid email is required';
        if (!customerData.phone) newErrors.phone = 'Phone is required';
        if (!customerData.address) newErrors.address = 'Address is required';
        if (!customerData.postcode) newErrors.postcode = 'Postcode is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setIsSubmitting(true);
            try {
                const response = await fetch(`${API_URL}/api/customer/update/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(customerData)
                });
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const data = await response.json();
                if (response.status === 200) {
                    setMessage({ status: true, reason: 'success', desc: 'Customer updated successfully' });
                    
                } else {
                    throw new Error(data.message || 'Failed to update customer');
                }
            } catch (error) {
                setMessage({ status: true, reason: 'error', desc: 'Failed to update customer: ' + error.message });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
      <MasterLayout>
          <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <h6 className="h6 mb-0">Edit Customer</h6>
                        </div>
                        <div className="card-body">
                            {/* Message Alert */}
                            {message.status && (
                                <div className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-4`} role="alert">
                                    {message.desc}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setMessage({ ...message, status: false })}
                                        aria-label="Close"
                                    ></button>
                                </div>
                            )}

                            {/* Edit Form */}
                            <div className="p-3 rounded">
                                
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label htmlFor="name" className="form-label fw-bold">Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            value={customerData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            className={`form-control form-control-lg ${errors.name ? 'is-invalid' : ''}`}
                                        />
                                        <div className="form-text">Enter the customer's full name.</div>
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="email" className="form-label fw-bold">Email</label>
                                        <input
                                            type="email"
                                            id="email"
                                            value={customerData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className={`form-control form-control-lg ${errors.email ? 'is-invalid' : ''}`}
                                        />
                                        <div className="form-text">e.g., customer@example.com</div>
                                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="phone" className="form-label fw-bold">Phone</label>
                                        <input
                                            type="text"
                                            id="phone"
                                            value={customerData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            className={`form-control form-control-lg ${errors.phone ? 'is-invalid' : ''}`}
                                        />
                                        <div className="form-text">e.g., +44 123 456 7890</div>
                                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="address" className="form-label fw-bold">Address</label>
                                        <input
                                            inputId="address"
                                            value={customerData.address}
                                            onChange={handleAddressChange}
                                            className={`form-control form-control-lg ${errors.address ? 'is-invalid' : ''}`}
                                            classNamePrefix="select"
                                            placeholder="Enter address"
                                        />
                                        <div className="form-text">Enter the customer's full address.</div>
                                        {errors.address && <div className="invalid-feedback d-block">{errors.address}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="postcode" className="form-label fw-bold">Postcode</label>
                                        <input
                                            type="text"
                                            id="postcode"
                                            value={customerData.postcode}
                                            onChange={(e) => handleInputChange('postcode', e.target.value)}
                                            className={`form-control form-control-lg ${errors.postcode ? 'is-invalid' : ''}`}
                                        />
                                        <div className="form-text">e.g., SW1A 1AA</div>
                                        {errors.postcode && <div className="invalid-feedback">{errors.postcode}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Status</label>
                                        <div className="form-check form-switch mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="statusSwitch"
                                                checked={customerData.status === 'active'}
                                                onChange={() => handleInputChange('status', customerData.status === 'active' ? 'inactive' : 'active')}
                                            />
                                            <label className="form-check-label" htmlFor="statusSwitch">
                                                {customerData.status === 'active' ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            <div className="d-flex justify-content-between mt-5">
                              
                                <button
                                    onClick={handleSubmit}
                                    className="btn btn-success btn-lg"
                                    disabled={isSubmitting}
                                >
                                    <Icon icon="simple-line-icons:check" className="me-2" />
                                    {isSubmitting ? 'Updating...' : 'Update Customer'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </MasterLayout>
    );
};

export default EditCustomerPage;